import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Please use at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please log in.");
      navigate("/login");
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="App">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col px-6 py-16">
        <div className="mb-8 text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-sugar-pink text-white"><Lock size={26} /></span>
          <h1 className="mt-4 font-display text-3xl font-extrabold text-sugar-ink">Set a new password</h1>
          <p className="mt-1 text-sm text-sugar-ink2">Choose a new password for your account.</p>
        </div>
        {!token ? (
          <div data-testid="reset-no-token" className="rounded-2xl border border-sugar-border bg-white p-7 text-center sugar-card-shadow">
            <p className="text-sm text-sugar-ink2">This reset link is missing or invalid. Please request a new one.</p>
            <Link to="/forgot-password" className="mt-4 inline-block font-bold text-sugar-pink">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-sugar-border bg-white p-7 sugar-card-shadow">
            {err && <p data-testid="reset-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600">{err}</p>}
            <div>
              <label className="mb-1.5 block text-sm font-bold text-sugar-ink">New password</label>
              <Input data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl border-sugar-border" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-bold text-sugar-ink">Confirm new password</label>
              <Input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-xl border-sugar-border" />
            </div>
            <Button data-testid="reset-submit" disabled={loading} type="submit" className="w-full rounded-full bg-sugar-pink py-6 font-bold text-white hover:bg-sugar-pinkhover">
              {loading ? "Saving..." : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
